import React, { useState, useEffect } from 'react';
import {
  Button,
  Title,
  Input,
  Label,
  Select,
  Option,
  TextArea,
  FlexBox,
  FlexBoxJustifyContent,
  FlexBoxAlignItems,
  MessageStrip,
  BusyIndicator,
  Dialog
} from '@ui5/webcomponents-react';
import { useNavigate } from 'react-router-dom';
import axiosInstance from '../../utils/axiosInstance';
import Navbar from '../Navbar';
import SideMenu from '../SideMenu';
import { formatNumberWithComma } from '../../utils/validator';
import withPermission from '../../components/withPermission';
import useAutoTranslate from '../../hooks/useAutoTranslate';


const CreateRamp = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    rampCode: '',
    physicalDockNumber: '',
    maxWeightCapacityTons: '',
    maxVolumeCapacityCubicMeters: '',
    warehouseId: '',
    rampType: '',
    warehouseOriginId: '',
    status: 'AVAILABLE',
    priority: '3',
    notes: ''
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [warehouses, setWarehouses] = useState([]);
  const [selectedWarehouse, setSelectedWarehouse] = useState('');
  const [loadingWarehouses, setLoadingWarehouses] = useState(false);

  const [isErrorDialogOpen, setIsErrorDialogOpen] = useState(false);
  const [errorDialogMessage, setErrorDialogMessage] = useState('');

  const statusOptions = [
    { value: 'AVAILABLE', label: 'Disponible' },
    { value: 'OCCUPIED', label: 'Ocupada' },
    { value: 'MAINTENANCE', label: 'Mantenimiento' }
  ];

  const priorityOptions = [
    { value: '1', label: '1' },
    { value: '2', label: '2' },
    { value: '3', label: '3' }
  ];

  const rampTypeOptions = [
    { value: 'Rampla 8 pies', label: 'Rampla 8 pies' },
    { value: 'Rampla 12 pies', label: 'Rampla 12 pies' },
    { value: 'Rampla doble', label: 'Rampla doble' },
  ];

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  const showErrorDialog = (message) => {
    setErrorDialogMessage(message);
    setIsErrorDialogOpen(true);
  };

  const fetchWarehouses = async () => {
    setLoadingWarehouses(true);
    try {
      const response = await axiosInstance.get('warehouses');
      setWarehouses(response.data.records.data);
    } catch (error) {
      console.error('Error fetching warehouses:', error);
      setError('Error al cargar la lista de almacenes');
    } finally {
      setLoadingWarehouses(false);
    }
  };

  useEffect(() => {
    fetchWarehouses();
  }, []);

  const { autoT } = useAutoTranslate();


  const handleChange = (e) => {
    const { name, value } = e.target;
    
    if (name === 'maxWeightCapacityTons' || name === 'maxVolumeCapacityCubicMeters') {
      const normalizedValue = value;
      setFormData({
        ...formData,
        [name]: normalizedValue
      });
    } else {
      setFormData({
        ...formData,
        [name]: value
      });
    }
  };

  const handleSelectChange = (e, fieldName) => {
    const selectedValue = e.detail.selectedOption.value;
    setFormData(prev => ({ ...prev, [fieldName]: selectedValue }));
    if (fieldName === 'warehouseOriginId') {
        setSelectedWarehouse(selectedValue);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(false);
    setIsErrorDialogOpen(false);
    setErrorDialogMessage('');

    if (!formData.warehouseId || !formData.rampType) {
        showErrorDialog('Por favor, seleccione Almacén y Tipo de Rampla.');
        setLoading(false);
        return;
    }

    try {
      const weightString = String(formData.maxWeightCapacityTons).replace(/,/g, '.');
      const volumeString = String(formData.maxVolumeCapacityCubicMeters).replace(/,/g, '.');

      const weight = parseFloat(weightString);
      const volume = parseFloat(volumeString);

      if (isNaN(weight) || weight <= 0) {
          showErrorDialog('El Peso Máximo debe ser un número válido y mayor que cero.');
          setLoading(false);
          return;
      }
      if (isNaN(volume) || volume <= 0) {
          showErrorDialog('El Volumen Máximo debe ser un número válido y mayor que cero.');
          setLoading(false);
          return;
      }

      const payload = {
        name: formData.name,
        rampCode: formData.rampCode,
        physicalDockNumber: formData.physicalDockNumber,
        maxWeightCapacityTons: weight,
        maxVolumeCapacityCubicMeters: volume,
        status: "AVAILABLE",
        priority: 1,
        rampType: formData.rampType,
        warehouseId: formData.warehouseId,
        warehouseOriginId: formData.warehouseOriginId,
        isActive: true
      };
      console.log("Payload", payload)
  
      const response = await axiosInstance.post('/virtual-ramps', payload);
  
      if (response.status === 200 && response.data?.data?.id) {
        setTimeout(() => navigate('/home', { state: { activePart: 'home', selectedTab: 3 } }), 1500);
        const newRampId = response.data.data.id;
        console.log("Rampla creada con ID:", newRampId);
        setSuccess(true);
      } else {
        const apiErrorMessage = response.data?.message || "Ocurrió un error al crear la rampla (respuesta inesperada).";
        console.error("Respuesta inválida o error al crear rampla:", response);
        showErrorDialog(apiErrorMessage);
      }
    } catch (err) {
      const networkErrorMessage = err.response?.data?.message || 'Error de red o servidor al crear la rampla.';
      console.error('Error creating ramp:', err);
      showErrorDialog(networkErrorMessage);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <BusyIndicator active size="Large" text={autoT("Creando rampla...")} />;
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <Navbar toggleSidebar={toggleSidebar} />
      
      <div style={{ display: 'flex', flex: 1, marginTop: '48px' }}>
        <SideMenu isOpen={sidebarOpen} />
        
        <div style={{ 
          flex: 1,
          marginLeft: sidebarOpen ? '250px' : '0',
          transition: 'margin-left 0.3s',
          padding: '20px',
          backgroundColor: '#f5f6f7'
        }}>
         <div style={{ 
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '20px',
            marginTop: '10px'
          }}>
          <Title level="H2" style={{ marginBottom: '1.5rem' }}>{autoT("Crear Rampla")}</Title>
          </div>
      {error && (
        <MessageStrip design="Negative" style={{ marginBottom: '1rem' }}>
          {autoT(error)}
        </MessageStrip>
      )}
      
      {success && (
        <MessageStrip design="Positive" style={{ marginBottom: '1rem' }}>
          {autoT("Rampla creada exitosamente! Redirigiendo al listado...")}
        </MessageStrip>
      )}
      <form onSubmit={handleSubmit}>
      <FlexBox style={{ gap: '1rem', marginBottom: '1rem' }}>

        <div style={{ flex: 1  }}>
          <Label required showColon>{autoT("Nombre")}</Label>
          <Input
            name="name"
            value={formData.name}
            onChange={handleChange}
            placeholder="Ej: Rampla Insumos Químicos"
            required
            style={{ width: '100%' }}
          />
        </div>
        
        <div style={{ flex: 1 }}>
            <Label required showColon>{autoT("Tipo de rampla")}</Label>
            <Select
              value={formData.rampType}
              onChange={(e) => handleSelectChange(e, 'rampType')}
              required
              style={{ width: '100%' }}
            >
              <Option value="">{autoT("Seleccione un tipo")}</Option>
              {rampTypeOptions.map(option => (
                <Option key={option.value} value={option.value}>
                  {option.label}
                </Option>
              ))}
            </Select>
          </div>
        </FlexBox>
        <FlexBox style={{ gap: '1rem', marginBottom: '1rem' }}>
          <div style={{ flex: 1 }}>
            <Label required showColon>{autoT("Código")}</Label>
            <Input
              name="rampCode"
              value={formData.rampCode}
              onChange={handleChange}
              placeholder="Ej: VR-COL01-04"
              required
              style={{ width: '100%' }}
            />
          </div>
          <div style={{ flex: 1 }}>
            <Label required showColon>{autoT("Muelle Físico")}</Label>
            <Input
              name="physicalDockNumber"
              value={formData.physicalDockNumber}
              onChange={handleChange}
              placeholder="Ej: D-COL01-04"
              required
              style={{ width: '100%' }}
            />
          </div>
        </FlexBox>

        <FlexBox style={{ gap: '1rem', marginBottom: '1rem' }}>
          <div style={{ flex: 1 }}>
            <Label required showColon>{autoT("Peso Máximo (kg)")}</Label>
            <Input
              name="maxWeightCapacityTons"
              value={formatNumberWithComma(formData.maxWeightCapacityTons)}
              onChange={handleChange}
              type="text"
              required
              style={{ width: '100%' }}
            />
          </div>
          <div style={{ flex: 1 }}>
            <Label required showColon>{autoT("Volumen Máximo (m³)")}</Label>
            <Input
              name="maxVolumeCapacityCubicMeters"
              value={formatNumberWithComma(formData.maxVolumeCapacityCubicMeters)}
              onChange={handleChange}
              type="text"
              required
              style={{ width: '100%' }}
            />
          </div>
        </FlexBox>
        <FlexBox style={{ gap: '1rem', marginBottom: '1rem' }}>
          <div style={{ flex: 1 }}>
            <Label required showColon>{autoT("Almacén Origen")}</Label>
            {loadingWarehouses ? (
              <BusyIndicator active size="Small" />
            ) : (
              <Select
                value={formData.warehouseOriginId}
                onChange={(e) => handleSelectChange(e, 'warehouseOriginId')}
                required
                style={{ width: '100%' }}
              >
                <Option value="">{autoT("Seleccione un almacén")}</Option>
                {warehouses.map(warehouse => (
                  <Option key={warehouse.id} value={warehouse.id}>
                    {warehouse.name || `Almacén ${warehouse.id}`}
                  </Option>
                ))}
              </Select>
            )}
          </div>
          <div style={{ flex: 1 }}>
            <Label required showColon>{autoT("Almacén Destino")}</Label>
            {loadingWarehouses ? (
              <BusyIndicator active size="Small" />
            ) : (
              <Select
                value={formData.warehouseId}
                onChange={(e) => handleSelectChange(e, 'warehouseId')}
                required
                style={{ width: '100%' }}
              >
                <Option value="">{autoT("Seleccione un almacén")}</Option>
                {warehouses.map(warehouse => (
                  <Option key={warehouse.id} value={warehouse.id}>
                    {warehouse.name || `Almacén ${warehouse.id}`}
                  </Option>
                ))}
              </Select>
            )}
          </div>
        </FlexBox>

        <FlexBox justifyContent={FlexBoxJustifyContent.End} style={{ gap: '0.5rem' }}>
          <Button design="Transparent" onClick={() => navigate('/ramps')}>
            {autoT("Cancelar")}
          </Button>
          <Button 
              design="Emphasized"
              onClick={handleSubmit}
              disabled={loading}
            >
              {loading ? autoT("Guardando...") : autoT("Guardar Rampla")}
            </Button>
        </FlexBox>
      </form>
    </div>
    </div>
    </div>
  );
};

export default withPermission(CreateRamp, 'virtualramp', 'create');